/**
 * @name: emailApi
 * @author: weiyong
 * @date: 2026-01-25
 * @description: 邮件相关API接口封装
 */

// @ts-ignore
import {QueryCommand} from "xmgoing-server";

/**
 * 通用的 API 请求方法
 * @param url - 接口地址
 * @param method - 请求方法
 * @param params - 请求参数
 * @param showTip - 是否显示提示
 */
const request = async (
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
  params?: any,
  showTip = true
) => {
  // @ts-ignore
  const queryCommand = new QueryCommand({
    url,
    method,
    showTip,
  });

  return await queryCommand.executeQueryPromise({ params });
};

/**
 * 发送邮件
 * @param emailData - 邮件数据
 */
export const sendEmail = async (emailData: any) => {
  // 关闭自动提示，由调用方控制提示内容
  return await request('/api/xm-email/mail/send', 'POST', emailData, false);
};

/**
 * 草稿箱发送邮件
 * @param emailData - 邮件数据
 */
export const sendEmailByDraft = async (emailData: any) => {
  // 关闭自动提示，由调用方控制提示内容
  return await request('/api/xm-email/mail/send-draft', 'POST', emailData, false);
};

/**
 * 保存草稿
 * @param draftData - 草稿数据
 */
export const saveDraft = async (draftData: any) => {
  return await request('/api/xm-email/mail/draft', 'POST', draftData);
};

/**
 * 获取邮件列表
 * @param params - 查询参数
 */
export const getEmailList = async (params: any) => {
  return await request('/api/xm-email/mail/list', 'POST', params);
};

/**
 * 获取已发送邮件列表
 * @param params - 查询参数（包含分页信息）
 */
export const getSentEmails = async (params: any) => {
  return await request('/api/xm-email/mail/sent', 'POST', params);
};

/**
 * 获取收件箱邮件列表
 * @param params - 查询参数（包含分页信息）
 */
export const getInboxEmails = async (params: any) => {
  return await request('/api/xm-email/mail/inbox', 'POST', params);
};

/**
 * 获取所有邮件列表
 * @param params - 查询参数（包含分页信息和 type 等筛选条件）
 */
export const getAllEmails = async (params: any) => {
  return await request('/api/xm-email/mail/all', 'POST', params);
};

/**
 * 获取草稿箱邮件列表
 * @param params - 查询参数（包含分页信息）
 */
export const getDraftEmails = async (params: any) => {
  return await request('/api/xm-email/mail/drafts', 'POST', params);
};

/**
 * 获取垃圾箱邮件列表
 * @param params - 查询参数（包含分页信息）
 */
export const getTrashEmails = async (params: any) => {
  return await request('/api/xm-email/mail/trash', 'POST', params);
};

/**
 * 获取待办邮件列表
 * @param params - 查询参数（包含分页信息）
 */
export const getTodoEmails = async (params: any) => {
  return await request('/api/xm-email/mail/todo', 'POST', params);
};

/**
 * 获取标星邮件列表
 * @param params - 查询参数（包含分页信息）
 */
export const getStarredEmails = async (params: any) => {
  return await request('/api/xm-email/mail/starred', 'POST', params);
};

/**
 * 获取标签邮件列表
 * @param params - 查询参数（包含分页信息和labelId）
 */
export const getLabelEmails = async (params: any,labelId?:string) => {
  if(labelId==null||labelId===''){
    return ;
  }
  return await request(`/api/xm-email/mail/label/${labelId}`, 'POST', params);
};

/**
 * 获取文件夹邮件列表
 * @param params - 查询参数（包含分页信息和folderId）
 * @param folderId - 文件夹ID
 */
export const getFolderEmails = async (params: any,folderId:string) => {
  return await request(`/api/xm-email//mail/folder/${folderId}`, 'POST', params);
};

/**
 * 获取邮件详情
 * @param id - 邮件ID
 */
export const getEmailDetail = async (id: string | number) => {
  return await request(`/api/xm-email/mail/detail/${id}`, 'GET', { id });
};

/**
 * 删除邮件（移动到垃圾箱）
 * @param ids - 邮件ID数组
 */
export const deleteEmails = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/delete', 'POST', { ids: idsStr });
};

/**
 * 永久删除邮件
 * @param ids - 邮件ID数组
 */
export const permanentDeleteEmails = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/permanentDelete', 'POST', { ids: idsStr });
};

/**
 * 清空垃圾箱
 */
export const emptyTrash = async () => {
  return await request('/api/xm-email/mail/trash/empty', 'POST');
};

/**
 * 撤回邮件
 * @param ids - 邮件ID数组
 */
/**
 * 撤回邮件
 * @param ids - 邮件ID数组
 * @param reason - 撤回原因
 */
export const recallEmails = async (ids: (string | number)[], reason: string) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/revoke', 'POST', { ids: idsStr, reason });
};

/**
 * 标记为已读/未读
 * @param ids - 邮件ID数组
 * @param isRead - 是否已读 (0:未读, 1:已读)
 */
export const markEmailsAsRead = async (ids: (string | number)[], isRead: 0 | 1) => {
  return await request('/xm-email/mail/markRead', 'POST', { ids, isRead });
};

/**
 * 标星/取消标星
 * @param ids - 邮件ID数组
 * @param isStarred - 是否标星 (0:否, 1:是)
 */
export const starEmails = async (ids: (string | number)[], isStarred: 0 | 1) => {
  return await request('/xm-email/mail/star', 'POST', { ids, isStarred });
};

/**
 * 标记为待办邮件
 * @param data - 待办数据 {ids: string[], todoDate?: string, addReminder?: number, remark?: string}
 */
export const markTodoEmails = async (data: { ids: (string | number)[], todoDate?: string, addReminder?: number, remark?: string }) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = data.ids.join(';');
  return await request('/api/xm-email/mail/mark-todo', 'POST', { 
    ids: idsStr,
    todoDate: data.todoDate,
    addReminder: data.addReminder,
    remark: data.remark
  });
};

/**
 * 取消待办
 * @param ids - 邮件ID数组
 */
export const cancelTodoEmails = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/cancel-todo', 'POST', { ids: idsStr });
};

/**
 * 标记为未读
 * @param ids - 邮件ID数组
 */
export const markUnreadEmails = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/mark-unread', 'POST', { ids: idsStr });
};

/**
 * 标记为已读
 * @param ids - 邮件ID数组
 */
export const markReadEmails = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/mark-read', 'POST', { ids: idsStr });
};

/**
 * 全部标为已读
 * @param type - 邮件类型：inbox/trash/todo/starred/all/folder/label
 * @param folderId - 文件夹ID（type=folder 时传递）
 * @param labelId - 标签ID（type=label 时传递）
 */
export const markReadAllEmails = async (type = 'inbox', folderId?: string, labelId?: string) => {
  const params: Record<string, any> = { type };
  if (type === 'folder' && folderId) {
    params.folderId = folderId;
  }
  if (type === 'label' && labelId) {
    params.labelId = labelId;
  }
  return await request('/api/xm-email/mail/mark-read-all', 'POST', params);
};

/**
 * 取消标星
 * @param ids - 邮件ID数组
 */
export const cancelStarEmails = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/cancel-star', 'POST', { ids: idsStr });
};

/**
 * 标记为标星
 * @param ids - 邮件ID数组
 */
export const markStarEmails = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/mark-star', 'POST', { ids: idsStr });
};

/**
 * 标记邮件为标签
 * @param ids - 邮件ID数组
 * @param labelId - 标签ID
 */
export const markLabelEmails = async (ids: (string | number)[], labelId: string | number) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/mark-label', 'POST', { ids: idsStr, labelId });
};

/**
 * 取消所有标签
 * @param ids - 邮件ID数组
 */
export const cancelAllLabels = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/cancel-all-labels', 'POST', { ids: idsStr });
};

/**
 * 新建文件夹并移动邮件
 * @param ids - 邮件ID数组
 * @param folderName - 文件夹名称
 */
export const addFolderAndMove = async (ids: (string | number)[], folderName: string) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mail/create-folder-and-move', 'POST', { ids: idsStr, folderName });
};

/**
 * 移动邮件到文件夹
 * @param ids - 邮件ID数组
 * @param folderId - 文件夹ID
 */
export const moveEmails = async (ids: (string | number)[], folderId: string | number) => {
  return await request('/xm-email/mail/move', 'POST', { ids, folderId });
};

/**
 * 移动邮件到文件夹（新接口）
 * @param ids - 邮件ID数组
 * @param folderId - 文件夹ID
 */
export const moveEmailsToFolder = async (ids: (string | number)[], folderId: string | number) => {
  let idsParam: string | (string | number)[];
  if(Array.isArray(ids)){
    idsParam = ids.join(';');
  } else {
    idsParam = ids;
  }
  return await request('/api/xm-email/mail/move-to-folder', 'POST', { ids: idsParam, folderId });
};

/**
 * 获取附件列表
 * @param emailId - 邮件ID
 */
export const getAttachments = async (emailId: string | number) => {
  return await request('/xm-email/attachment/list', 'GET', { emailId });
};

/**
 * 下载附件
 * @param attachmentId - 附件ID
 */
export const downloadAttachment = async (attachmentId: string | number) => {
  return await request('/xm-email/attachment/download', 'GET', { attachmentId });
};

/**
 * 搜索邮件
 * @param keyword - 搜索关键词
 * @param params - 其他查询参数
 */
export const searchEmails = async (keyword: string, params?: any) => {
  return await request('/xm-email/mail/search', 'POST', { keyword, ...params });
};

/**
 * 获取常用组列表
 */
export const getCommonGroups = async () => {
  return await request('/api/xm-email/mail/group/list', 'POST', undefined, false);
};

/**
 * 获取常用组列表 和 用户信息
 */
export const getCommonGroupsAndUsers = async () => {
  return await request('/api/xm-email/mail/group/list-detail', 'POST', undefined, false);
};

/**
 * 创建常用组
 * @param groupData - 常用组数据 {groupName: string, members: Array<{memberUserId: string, memberName: string}>}
 */
export const createCommonGroup = async (groupData: any) => {
  return await request('/api/xm-email/mail/group/add', 'POST', groupData);
};

/**
 * 更新常用组
 * @param groupData - 常用组数据 {id: string | number, groupName: string, members?: any[]}
 */
export const updateCommonGroup = async (groupData: {id: string | number, groupName: string, members?: any[]}) => {
  return await request('/api/xm-email/mail/group/update', 'POST', groupData);
};

/**
 * 删除常用组
 * @param id - 常用组ID
 */
export const deleteCommonGroup = async (id: string | number) => {
  return await request('/api/xm-email/mail/group/remove', 'POST', { ids:id });
};

/**
 * 同步常用组成员（全量传入，后端以此为准）
 * @param groupId - 常用组ID
 * @param members - 全量成员数据数组 [{memberUserId: string, memberName: string}]
 */
export const addMembersToGroup = async (groupId: string | number, members: any[]) => {
  return await request('/api/xm-email/mail/group/member/sync', 'POST', { groupId, members });
};

/**
 * 从常用组中移除成员
 * @param groupId - 常用组ID
 * @param memberUserId - 成员用户ID
 */
export const removeMemberFromGroup = async (groupId: string | number, memberUserId: string) => {
  return await request('/api/xm-email/mail/group/removeMember', 'POST', { groupId, memberUserId });
};

/**
 * 获取我的邮件配置信息
 */
export const getMyMailSettings = async () => {
  return await request('/api/xm-email/mailSetting/get-my-setting', 'GET', undefined, false);
};

/**
 * 保存我的邮件配置信息
 * @param settingsData - 配置数据
 */
export const saveMyMailSettings = async (settingsData: any) => {
  return await request('/api/xm-email/mailSetting/my-setting', 'POST', settingsData, false);
};

/**
 * 获取邮件阅读状态详情
 * @param mailId - 邮件ID
 */
export const getMailReadStats = async (mailId: string | number) => {
  return await request(`/api/xm-email/mail/read-stats/${mailId}`, 'POST', undefined, false);
};

/**
 * ====================================
 * 邮件文件夹管理相关API
 * ====================================
 */

/**
 * 获取文件夹详情
 * @param id - 文件夹ID
 */
export const getFolderDetail = async (id: string | number) => {
  return await request(`/api/xm-email/mailFolder/detail`, 'POST', { id }, false);
};

/**
 * 获取文件夹统计信息
 */
export const getFolderStats = async () => {
  return await request('/api/xm-email/mailFolder/stats', 'GET', undefined, false);
};

/**
 * 获取文件夹列表
 * @param params - 查询参数（包含分页信息）
 */
export const getFolderList = async (params?: any) => {
  return await request('/api/xm-email/mailFolder/listData', 'POST', params, false);
};

/**
 * 获取文件夹列表数据（带分页）
 * @param params - 查询参数（包含分页信息）
 */
export const getFolderListData = async (params?: any) => {
  return await request('/api/xm-email/mailFolder/listData', 'POST', params, false);
};

/**
 * 新增文件夹
 * @param folderData - 文件夹数据 {folderName: string, remark?: string}
 */
export const addFolder = async (folderData: { folderName: string; remark?: string }) => {
  return await request('/api/xm-email/mailFolder/add', 'POST', folderData);
};

/**
 * 更新文件夹
 * @param folderData - 文件夹数据 {id: string | number, folderName: string, remark?: string, folderType?: number}
 */
export const updateFolder = async (folderData: { id: string | number; folderName: string; remark?: string; folderType?: number }) => {
  return await request('/api/xm-email/mailFolder/update', 'POST', folderData);
};

/**
 * 删除文件夹
 * @param ids - 文件夹ID（多个用分号分隔的字符串）
 */
export const removeFolder = async (ids: string) => {
  return await request('/api/xm-email/mailFolder/remove', 'POST', { ids });
};

/**
 * 导出文件夹数据
 * @param ids - 文件夹ID数组
 */
export const exportFolder = async (ids?: (string | number)[]) => {
  const idsStr = ids ? ids.join(';') : '';
  return await request('/api/xm-email/mailFolder/export', 'POST', { ids: idsStr }, false);
};

/**
 * 导出文件夹模板
 */
export const exportFolderTemplate = async () => {
  return await request('/api/xm-email/mailFolder/exportTemplate', 'POST', undefined, false);
};

/**
 * 导入文件夹数据
 * @param fileData - 文件数据
 */
export const importFolder = async (fileData: any) => {
  return await request('/api/xm-email/mailFolder/import', 'POST', fileData);
};

/**
 * ====================================
 * 邮件模版管理相关API
 * ====================================
 */

/**
 * 获取模版详情
 * @param id - 模版ID
 */
export const getTemplateDetail = async (id: string | number) => {
  return await request('/api/xm-email/mailTemplate/detail', 'GET', { id }, false);
};

/**
 * 获取模版列表
 * @param params - 查询参数
 */
export const getTemplateList = async (params?: any) => {
  return await request('/api/xm-email/mailTemplate/list', 'GET', params, false);
};

/**
 * 获取模版列表数据（带分页）
 * @param params - 查询参数（包含分页信息）
 */
export const getTemplateListData = async (params?: any) => {
  return await request('/api/xm-email/mailTemplate/listData', 'POST', params, false);
};

/**
 * 新增模版
 * @param templateData - 模版数据 {templateName: string, subject: string, content: string, remark?: string, isDefault?: boolean}
 */
export const addTemplate = async (templateData: {
  templateName: string;
  subject: string;
  content: string;
  remark?: string;
  isDefault?: boolean;
}) => {
  return await request('/api/xm-email/mailTemplate/add', 'POST', templateData);
};

/**
 * 更新模版
 * @param templateData - 模版数据 {id: string | number, templateName: string, subject: string, content: string, remark?: string, isDefault?: boolean}
 */
export const updateTemplate = async (templateData: {
  id: string | number;
  templateName: string;
  subject: string;
  content: string;
  remark?: string;
  isDefault?: boolean;
}) => {
  return await request('/api/xm-email/mailTemplate/update', 'POST', templateData);
};

/**
 * 删除模版
 * @param ids - 模版ID（多个用分号分隔的字符串）
 */
export const removeTemplate = async (ids: string) => {
  return await request('/api/xm-email/mailTemplate/remove', 'POST', { ids });
};

/**
 * 导出模版数据
 * @param ids - 模版ID数组
 */
export const exportTemplate = async (ids?: (string | number)[]) => {
  const idsStr = ids ? ids.join(';') : '';
  return await request('/api/xm-email/mailTemplate/export', 'POST', { ids: idsStr }, false);
};

/**
 * 导出模版模板
 */
export const exportTemplateTemplate = async () => {
  return await request('/api/xm-email/mailTemplate/exportTemplate', 'POST', undefined, false);
};

/**
 * 导入模版数据
 * @param fileData - 文件数据
 */
export const importTemplate = async (fileData: any) => {
  return await request('/api/xm-email/mailTemplate/import', 'POST', fileData);
};

/**
 * ====================================
 * 邮件标签管理相关API
 * ====================================
 */

/**
 * 获取标签详情
 * @param id - 标签ID
 */
export const getLabelDetail = async (id: string | number) => {
  return await request(`/api/xm-email/mailLabel/detail`, 'GET', { id }, false);
};

/**
 * 获取标签列表（不分页）
 * @param params - 查询参数
 */
export const getLabelList = async (params?: any) => {
  return await request('/api/xm-email/mailLabel/list', 'POST', params, false);
};

/**
 * 获取标签列表数据（带分页）
 * @param params - 查询参数（包含分页信息）
 */
export const getLabelListData = async (params?: any) => {
  return await request('/api/xm-email/mailLabel/listData', 'POST', params, false);
};

/**
 * 新增标签
 * @param labelData - 标签数据 {labelName: string, labelColor?: string, remark?: string}
 */
export const addLabel = async (labelData: { labelName: string; labelColor?: string; remark?: string }) => {
  return await request('/api/xm-email/mailLabel/add', 'POST', labelData);
};

/**
 * 更新标签
 * @param labelData - 标签数据 {id: string | number, labelName: string, labelColor?: string, remark?: string}
 */
export const updateLabel = async (labelData: { id: string | number; labelName: string; labelColor?: string; remark?: string }) => {
  return await request('/api/xm-email/mailLabel/update', 'POST', labelData);
};

/**
 * 删除标签
 * @param ids - 标签ID（多个用分号分隔的字符串）
 */
export const removeLabel = async (ids: string) => {
  return await request('/api/xm-email/mailLabel/remove', 'POST', { ids });
};

/**
 * 移除邮件的标签
 * @param mailId - 邮件ID
 * @param tagId - 标签ID
 */
export const removeMailTag = async (mailId: string | number, tagId: string | number) => {
  return await request('/api/xm-email/mail/removeTag', 'POST', { id: mailId, tagId });
};

/**
 * 导出标签数据
 * @param ids - 标签ID数组
 */
export const exportLabel = async (ids?: (string | number)[]) => {
  const idsStr = ids ? ids.join(';') : '';
  return await request('/api/xm-email/mailLabel/export', 'POST', { ids: idsStr }, false);
};

/**
 * 导出标签模板
 */
export const exportLabelTemplate = async () => {
  return await request('/api/xm-email/mailLabel/exportTemplate', 'POST', undefined, false);
};

/**
 * 导入标签数据
 * @param fileData - 文件数据
 */
export const importLabel = async (fileData: any) => {
  return await request('/api/xm-email/mailLabel/import', 'POST', fileData);
};

/**
 * 获取最近联系人列表
 * @param params - 查询参数（包含分页信息）
 */
export const getRecentContacts = async (params?: any) => {
  return await request('/api/xm-email/mail/contact/recent', 'POST', params, false);
};

/**
 * 获取当前用户同部门用户列表
 * @param params - 查询参数（包含分页信息）
 */
export const getDepartmentUsers = async (params?: any) => {
  return await request('/api/pe-portal/users/v2/curDeptUser', 'GET', params, false);
};

/**
 * 获取当前用户的下属用户列表
 * @param params - 查询参数（包含分页信息）
 */
export const getSubordinateUsers = async (params?: any) => {
  return await request('/api/pe-portal/users/v2/subDeptUser', 'GET', params, false);
};

/**
 * 获取附件中心列表
 * @param params - 查询参数（包含分页信息）
 */
export const getAttachmentCenterList = async (params?: any) => {
  return await request('/api/xm-email/mailAttachment/center/list', 'POST', params, false);
};

/**
 * 保存附件到附件中心
 * @param attachmentId - 附件ID
 */
export const saveToAttachmentCenter = async (attachmentId: string | number) => {
  return await request(`/api/xm-email/mailAttachment/save-to-center/${attachmentId}`, 'POST', undefined, false);
};

/**
 * 高级查询邮件（根据不同类型调用不同接口）
 * @param params - 查询参数
 * @param type - 邮件类型 (inbox/sent/drafts/trash/todo/star-mail/wait-mail/all)
 * @param folderId - 文件夹ID（type为folder时必传）
 * @param labelId - 标签ID（type为label时必传）
 */
export const advancedSearchInbox = async (
  params: {
    subject?: string;           // 主题
    sender?: string;            // 发件人
    receiver?: string;          // 收件人
    plainContent?: string;      // 正文内容
    receiveTime?: string;       // 接收时间类型 (today/yesterday/week/month/custom)
    startDate?: string;         // 开始日期 (receiveTime为custom时使用)
    endDate?: string;           // 结束日期 (receiveTime为custom时使用)
    mailStatus?: string;        // 邮件状态 (urgent/todo/normal)
    hasAttachment?: string;     // 是否有附件 (1:有, 0:无)
    isStar?: string;            // 是否标星 (1:是, 0:否)
    current?: number;           // 当前页
    size?: number;              // 每页条数
  },
  type = 'inbox',
  folderId?: string,
  labelId?: string
) => {
  // 根据 type 构建不同的接口路径
  let apiPath = '';
  switch (type) {
    case 'inbox':
      apiPath = '/api/xm-email/mail/inbox/advanced';
      break;
    case 'sent':
      apiPath = '/api/xm-email/mail/sent/advanced';
      break;
    case 'drafts':
      apiPath = '/api/xm-email/mail/drafts/advanced';
      break;
    case 'trash':
      apiPath = '/api/xm-email/mail/trash/advanced';
      break;
    case 'todo':
      apiPath = '/api/xm-email/mail/todo/advanced';
      break;
    case 'starred ':
      apiPath = '/api/xm-email/mail/starred/advanced';
      break;
    case 'all':
      apiPath = '/api/xm-email/mail/all/advanced';
      break;
    case 'folder':
      if (!folderId) {
        console.error('[advancedSearchInbox] folder类型必须传入folderId');
        return Promise.reject(new Error('folder类型必须传入folderId'));
      }
      apiPath = `/api/xm-email/mail/folder/${folderId}/advanced`;
      break;
    case 'label':
      if (!labelId) {
        console.error('[advancedSearchInbox] label类型必须传入labelId');
        return Promise.reject(new Error('label类型必须传入labelId'));
      }
      apiPath = `/api/xm-email/mail/label/${labelId}/advanced`;
      break;
    default:
      console.warn(`[advancedSearchInbox] 未知的type: ${type}，使用默认inbox接口`);
      apiPath = '/api/xm-email/mail/inbox/advanced';
      break;
  }
  return await request(apiPath, 'POST', params, false);
};

/**
 * 批量删除附件中心附件
 * @param ids - 附件ID数组
 */
export const removeAttachmentCenter = async (ids: (string | number)[]) => {
  // 将 ids 数组转换为分号分隔的字符串
  const idsStr = ids.join(';');
  return await request('/api/xm-email/mailAttachment/center/delete', 'POST', { ids: idsStr });
};
